import { ListboxOption, ListboxOptions } from '@headlessui/react';
import React from 'react';
import { spacing } from '../../styles/spacing.css';
import { getPixelValue } from '../../utils/units';
import { Layout } from '../Layout/Layout';
import { DrawerSelectContent } from './DrawerSelect';
import { Item } from './Item';
import type { ItemProps, Key } from './Item';
import { SELECT_ITEM_HEIGHT } from './Item.css';
import type { SelectProps } from './Select';
import * as styles from './Select.css';
import type { SelectItem } from './useSelect';

export const MAX_VISIBLE_CONTENT_ITEMS = 5;
export const POPOVER_OFFSET = 5;
export const ANCHOR_OFFSET = -1;

type ContentProps = Pick<SelectProps, 'limitWidth' | 'maxVisibleContentItems' | 'placeholder'> & {
  readonly items: SelectProps['items'];
  readonly itemsWithKey: SelectItem[];
  readonly isMobileViewport: boolean;
  readonly onAction?: (itemValue: Key) => void;
  readonly isSelectedItem: (item: ItemProps) => boolean;
  readonly triggerWidth: number;
};

type SelectOptionsProps = {
  readonly maxVisibleContentItems: Required<ContentProps>['maxVisibleContentItems'];
  readonly limitWidth: Required<ContentProps>['limitWidth'];
  readonly itemsWithKey: SelectItem[];
  readonly triggerWidth: number;
};

const SelectOptions: React.FC<SelectOptionsProps> = ({
  itemsWithKey,
  maxVisibleContentItems,
  limitWidth,
  triggerWidth,
}) => {
  const height =
    getPixelValue(SELECT_ITEM_HEIGHT) * Math.min(maxVisibleContentItems, itemsWithKey.length) +
    getPixelValue(styles.SELECT_PADDING_BLOCK) * 2 +
    styles.SELECT_BORDER_WIDTH * 2;

  return (
    <ListboxOptions
      anchor={{
        gap: POPOVER_OFFSET,
        to: 'bottom start',
        offset: ANCHOR_OFFSET,
        padding: spacing.s24,
      }}
      as={Layout}
      className={styles.selectContent({ limitWidth })}
      height={`${height}px`}
      style={{
        width: limitWidth ? `${triggerWidth}px` : undefined,
        minWidth: limitWidth ? `${triggerWidth}px` : undefined,
      }}
      zIndex={2}
    >
      {itemsWithKey.map(item => (
        <ListboxOption aria-label={item.label} disabled={item.disabled} key={item.key} value={item.value}>
          {({ focus, selected }) => (
            <Item disabled={item.disabled} focused={focus} selected={selected} value={item.value}>
              {item.label}
            </Item>
          )}
        </ListboxOption>
      ))}
    </ListboxOptions>
  );
};

export const Content: React.FC<ContentProps> = ({
  itemsWithKey,
  maxVisibleContentItems = MAX_VISIBLE_CONTENT_ITEMS,
  limitWidth = true,
  isMobileViewport,
  placeholder,
  onAction,
  isSelectedItem,
  triggerWidth,
}) => {
  if (isMobileViewport) {
    return (
      <DrawerSelectContent
        isSelectedItem={isSelectedItem}
        itemsWithKey={itemsWithKey}
        onAction={onAction as (itemValue: Key) => void}
        placeholder={placeholder}
      />
    );
  }

  return (
    <SelectOptions
      itemsWithKey={itemsWithKey}
      limitWidth={limitWidth}
      maxVisibleContentItems={maxVisibleContentItems}
      triggerWidth={triggerWidth}
    />
  );
};
